import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./api";
import { blobToBase64 } from "./photo";
import { toast } from "sonner";
import type { AppointmentRow } from "./queries";
import type { TemplateMedicine } from "@emr/shared";

const q = () => useQueryClient();

type ToastErr = (msg: string) => void;
const onError = (err: unknown, fallback: string) => {
  const e = err as Error & { body?: { error?: unknown; message?: unknown } };
  // Prefer the server's human-readable message over its machine error code
  // (routes return both: { error: "template_name_taken", message: "A template with this name already exists" }).
  // zod-validator 400s return the raw zod result { success, error: ZodError } —
  // body.error is then an OBJECT (serializes as {name, message}), which React
  // cannot render as a toast child. Coerce non-string candidates safely.
  const candidates = [e?.body?.message, e?.body?.error, e?.message];
  for (const c of candidates) {
    if (typeof c === "string" && c) {
      toast.error(c);
      return;
    }
    // ZodError-like object: surface its .message string if present.
    if (c && typeof c === "object" && typeof (c as { message?: unknown }).message === "string") {
      toast.error((c as { message: string }).message);
      return;
    }
  }
  toast.error(fallback);
};

export function useCreateAppointment() {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch<{ appointment: unknown }>("/appointments", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["appointments"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success("Appointment booked"); },
    onError: (e) => {
      const err = e as Error & { body?: { error?: string } };
      // already_booked_today is handled inline by the BookDialog (it renders
      // an alert with the existing appt's slot details and refuses to close).
      // Suppress the generic toast so the user isn't shown two messages.
      if (err?.body?.error === "already_booked_today") return;
      onError(e, "Failed to book appointment");
    },
  });
}

export function useConfirmAppointment(id: string) {
  const qc = q();
  return useMutation({
    // slot: any key of the target date's effective config (Phase 2 generalized
    // the confirm route; Phase 3 sends config keys).
    mutationFn: (body: { slot: string; arrivalTime: string }) => apiFetch<{ appointment: unknown }>(`/appointments/${id}/confirm`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["appointments"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success("Appointment confirmed"); },
    onError: (e) => onError(e, "Failed to confirm appointment"),
  });
}

export function useReorderAppointment(_id?: string) {
  const qc = q();
  return useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: "up" | "down" }) => {
      return apiFetch<{ appointment: unknown }>(`/appointments/${id}/reorder`, { method: "PATCH", body: JSON.stringify({ direction }) });
    },
    // Optimistic update: swap queueOrder values in the cache immediately so
    // the UI reorders instantly without waiting for the server round-trip.
    onMutate: async ({ id, direction }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update.
      await qc.cancelQueries({ queryKey: ["appointments"], exact: false, predicate: (q) => q.queryKey[1] !== "status" });

      // Snapshot the current cache for rollback.
      const queryCache = qc.getQueriesData<{ rows: AppointmentRow[] }>({ queryKey: ["appointments"], exact: false, predicate: (q) => q.queryKey[1] !== "status" });

      // For each matching query, swap the queueOrder of the target row and its neighbour.
      for (const [key, data] of queryCache) {
        if (!data?.rows) continue;
        const rows = data.rows;
        const idx = rows.findIndex((r) => r.id === id);
        if (idx < 0) continue;
        const targetIdx = direction === "up" ? idx - 1 : idx + 1;
        if (targetIdx < 0 || targetIdx >= rows.length) continue;
        const a = rows[idx];
        const b = rows[targetIdx];
        const newRows = [...rows];
        newRows[idx] = { ...a, queueOrder: b.queueOrder };
        newRows[targetIdx] = { ...b, queueOrder: a.queueOrder };
        // Re-sort by queueOrder to reflect the new order.
        newRows.sort((x, y) => (x.queueOrder ?? 0) - (y.queueOrder ?? 0));
        qc.setQueryData(key, { ...data, rows: newRows });
      }

      return { queryCache };
    },
    onError: (_e, _vars, ctx) => {
      // Rollback on error.
      if (ctx?.queryCache) {
        for (const [key, data] of ctx.queryCache) {
          qc.setQueryData(key, data);
        }
      }
      onError(_e, "Failed to reorder");
    },
    onSuccess: () => {
      // Quiet background refetch to confirm the server state matches.
      qc.invalidateQueries({ queryKey: ["appointments"], exact: false, predicate: (q) => q.queryKey[1] !== "status" });
    },
  });
}

// Day reorder (Phase 3, 2026-10-18) — the CHECKED-IN rows' cross-slot calling
// order. PATCH /appointments/:id/day-reorder swaps adjacent day_serial values
// across slot boundaries (admin hard-gate server-side; boundary moves return
// { ok: true, moved: false } — a no-op, not an error, so nothing special is
// done on moved:false). The appointments list is already sorted by
// day_serial NULLS LAST, so a plain invalidate re-renders the new order.
export function useDayReorderAppointment() {
  const qc = q();
  return useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: "up" | "down" }) =>
      apiFetch<{ ok: boolean; moved: boolean }>(`/appointments/${id}/day-reorder`, {
        method: "PATCH",
        body: JSON.stringify({ direction }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["appointments"], exact: false, predicate: (q) => q.queryKey[1] !== "status" });
    },
    onError: (e) => onError(e, "Failed to reorder"),
  });
}

export function useRebookAppointment(id: string) {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch<{ appointment: unknown }>(`/appointments/${id}/rebook`, { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["appointments"] }); toast.success("Appointment rescheduled"); },
    onError: (e) => onError(e, "Failed to reschedule"),
  });
}

export function useCreateSlotBlock() {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch<{ block: unknown; moved: unknown[] }>("/slot-blocks", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["slot-blocks"] }); qc.invalidateQueries({ queryKey: ["appointments"] }); toast.success("Override saved"); },
    onError: (e) => onError(e, "Failed to save override"),
  });
}

export function useDeleteSlotBlock() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/slot-blocks/${id}`, { method: "DELETE" }),
    // "Override removed" — this hook is also used mid-edit (replace flow:
    // POST the replacement, then DELETE the old row), where "Block removed"
    // read as if the user's edit had been undone.
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["slot-blocks"] }); toast.success("Override removed"); },
    onError: (e) => onError(e, "Failed to remove override"),
  });
}

export function useUpdateSlotBlock(id: string) {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch(`/slot-blocks/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["slot-blocks"] }); qc.invalidateQueries({ queryKey: ["appointments"] }); toast.success("Override updated"); },
    onError: (e) => onError(e, "Failed to update override"),
  });
}

export function useCreatePatient() {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch<{ patient: { id: string; clinicUhid: string } }>("/patients", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["patients"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success("Patient created"); },
    onError: (e) => onError(e, "Failed to create patient"),
  });
}

// Duplicate-patient pre-check (2026-10-28) -- reception double-entry guard.
// Advisory: returns existing patients with the same mobile suffix + similar
// name + similar age. The add-patient form shows matches in a confirmation
// dialog before proceeding; creation is never blocked (families share mobiles).
export function useDuplicatePatientCheck() {
  return useMutation({
    mutationFn: (entry: { name: string; phone?: string; altPhone?: string; dob?: string; ageYears?: number; ageMonths?: number }) =>
      apiFetch<{ matches: unknown[] }>("/patients/duplicate-check", { method: "POST", body: JSON.stringify(entry) }),
    // Errors surface via the form's own catch (the check is best-effort --
    // a transient failure must not block patient creation).
    onError: () => undefined,
  });
}

export function useUpdatePatient(id: string) {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch<{ patient: unknown }>(`/patients/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["patients"] }); toast.success("Patient updated"); },
    onError: (e) => onError(e, "Failed to update patient"),
  });
}

export function useDeletePatient() {
  const qc = q();
  return useMutation({
    // Id at call time so the patients LIST can reuse one mutation for any row.
    // Server-side this is a HARD delete (2026-09-21): FK cascades wipe the
    // patient's entire clinical history, so every cached query family that
    // may reference the patient is invalidated.
    mutationFn: (id: string) => apiFetch(`/patients/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["patients"] });
      qc.invalidateQueries({ queryKey: ["appointments"] });
      qc.invalidateQueries({ queryKey: ["visits"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Patient deleted");
    },
    onError: (e) => onError(e, "Failed to delete patient"),
  });
}

// ---- Patient passport photo -------------------------------------------------
// Upload/replace and remove. The API gates the first upload behind
// photos.create and replacement behind photos.edit (admin bypasses both).
// useUploadPatientPhoto takes the patientId at CALL time — the add-patient
// form only learns the ID after POST /patients returns.

export function useUploadPatientPhoto() {
  const qc = q();
  return useMutation({
    mutationFn: ({ patientId, blob }: { patientId: string; blob: Blob }) =>
      blobToBase64(blob).then((base64) =>
        apiFetch<{ patient: unknown }>(`/patients/${patientId}/photo`, {
          method: "POST",
          body: JSON.stringify({ base64 }),
        }),
      ),
    onSuccess: (_r, { patientId }) => {
      qc.invalidateQueries({ queryKey: ["patients", patientId] });
      qc.invalidateQueries({ queryKey: ["patient-photo", patientId] });
      // Aggregates that embed photoR2Key (visit page header, appointments
      // rows → CheckInDialog thumb) refetch so the new key propagates.
      qc.invalidateQueries({ queryKey: ["visits"] });
      qc.invalidateQueries({ queryKey: ["appointments"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Photo saved");
    },
    onError: (e) => onError(e, "Failed to save photo"),
  });
}

export function useDeletePatientPhoto(patientId: string) {
  const qc = q();
  return useMutation({
    mutationFn: () => apiFetch(`/patients/${patientId}/photo`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["patients", patientId] });
      qc.invalidateQueries({ queryKey: ["patient-photo", patientId] });
      qc.invalidateQueries({ queryKey: ["visits"] });
      qc.invalidateQueries({ queryKey: ["appointments"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Photo removed");
    },
    onError: (e) => onError(e, "Failed to remove photo"),
  });
}

export function useAttachPatientFlag(patientId: string) {
  const qc = q();
  return useMutation({
    mutationFn: (flagId: string) => apiFetch(`/patients/${patientId}/flags`, { method: "POST", body: JSON.stringify({ flagId }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["patients", patientId, "flags"] }); toast.success("Flag added"); },
    onError: (e) => onError(e, "Failed to add flag"),
  });
}

export function useRemovePatientFlag(patientId: string) {
  const qc = q();
  return useMutation({
    mutationFn: (flagId: string) => apiFetch(`/patients/${patientId}/flags/${flagId}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["patients", patientId, "flags"] }); toast.success("Flag removed"); },
    onError: (e) => onError(e, "Failed to remove flag"),
  });
}

export function useCreateVisit() {
  const qc = q();
  return useMutation({
    // phoneConfirm: the mobile number entered at check-in — server verifies it
    // against the patient's registered phone/alt_phone before creating the visit.
    mutationFn: (body: { appointmentId: string; patientId: string; doctorId?: string; phoneConfirm: string }) => apiFetch<{ visit: { id: string } }>("/visits", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["appointments"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success("Checked in"); },
    onError: (e) => onError(e, "Failed to check in"),
  });
}

export function useRecordVitals(visitId: string) {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch(`/visits/${visitId}/vitals`, { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["appointments"] }); toast.success("Vitals recorded"); },
    onError: (e) => onError(e, "Failed to record vitals"),
  });
}

function crud(name: string, key: string) {
  const qc = q();
  return {
    create: useMutation({
      mutationFn: (body: Record<string, unknown>) => apiFetch(`/${name}`, { method: "POST", body: JSON.stringify(body) }),
      onSuccess: () => { qc.invalidateQueries({ queryKey: [key] }); toast.success(`${name.slice(0,1).toUpperCase()+name.slice(1,-1)} created`); },
      onError: (e) => onError(e, "Failed to create"),
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => apiFetch(`/${name}/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
      onSuccess: () => { qc.invalidateQueries({ queryKey: [key] }); toast.success("Updated"); },
      onError: (e) => onError(e, "Failed to update"),
    }),
    remove: useMutation({
      mutationFn: (id: string) => apiFetch(`/${name}/${id}`, { method: "DELETE" }),
      onSuccess: () => { qc.invalidateQueries({ queryKey: [key] }); toast.success("Deleted"); },
      onError: (e) => onError(e, "Failed to delete"),
    }),
  };
}

export const useCreateDrug = () => crud("drugs", "drugs").create;
export const useUpdateDrug = () => crud("drugs", "drugs").update;
export const useDeleteDrug = () => crud("drugs", "drugs").remove;

export function useBulkDeleteDrugs() {
  const qc = q();
  return useMutation({
    mutationFn: (ids: string[]) => apiFetch<{ deleted: number; requested: number }>("/drugs/bulk-delete", { method: "POST", body: JSON.stringify({ ids }) }),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ["drugs"] }); toast.success(`Deleted ${res.deleted} drug${res.deleted === 1 ? "" : "s"}`); },
    onError: (e) => onError(e, "Failed to delete drugs"),
  });
}

export function useBulkImportDrugs() {
  const qc = q();
  return useMutation({
    mutationFn: (rows: { name: string; composition?: string; formulation?: string; strength?: string }[]) =>
      apiFetch<{ inserted: number; updated: number; total: number }>("/drugs/bulk", { method: "POST", body: JSON.stringify({ rows }) }),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ["drugs"] }); toast.success(`Bulk import complete: ${res.inserted} inserted, ${res.updated} updated`); },
    onError: (e) => onError(e, "Bulk import failed"),
  });
}
export const useCreateService = () => crud("services", "services").create;
export const useUpdateService = () => crud("services", "services").update;
export const useDeleteService = () => crud("services", "services").remove;
export const useCreateFlag = () => crud("flags", "flags").create;
export const useUpdateFlag = () => crud("flags", "flags").update;
export const useDeleteFlag = () => crud("flags", "flags").remove;

// ---------------- EMR Visit ----------------

export function useSaveReview(visitId: string) {
  const qc = q();
  return useMutation({
    mutationFn: (body: { reviewNeeded: boolean; reviewDate?: string | null }) =>
      apiFetch<{ visit: unknown }>(`/visits/${visitId}/review`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["visits", visitId] }); qc.invalidateQueries({ queryKey: ["appointments"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success("Review saved"); },
    onError: (e) => onError(e, "Failed to save review"),
  });
}

export function useCreatePrescription(visitId: string) {
  const qc = q();
  return useMutation({
    mutationFn: (body: { medicines: Record<string, unknown>[] }) =>
      apiFetch<{ prescription: unknown }>(`/visits/${visitId}/prescriptions`, { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["visits", visitId] }); qc.invalidateQueries({ queryKey: ["visits", visitId, "prescriptions"] }); qc.invalidateQueries({ queryKey: ["patients"] }); toast.success("Prescription saved"); },
    onError: (e) => onError(e, "Failed to save prescription"),
  });
}

export function useCreateLabOrder(visitId: string) {
  const qc = q();
  return useMutation({
    mutationFn: (body: { tests: { testName: string; notes?: string | null }[] }) =>
      apiFetch<{ labOrder: unknown }>(`/visits/${visitId}/lab-orders`, { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["visits", visitId, "lab-orders"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success("Lab order created"); },
    onError: (e) => onError(e, "Failed to create lab order"),
  });
}

export function useUpdateLabTest(visitId: string) {
  const qc = q();
  return useMutation({
    mutationFn: ({ testId, body }: { testId: string; body: Record<string, unknown> }) =>
      apiFetch<{ labTest: unknown }>(`/visits/lab-tests/${testId}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["visits", visitId, "lab-orders"] }); toast.success("Lab test updated"); },
    onError: (e) => onError(e, "Failed to update lab test"),
  });
}

// ---------------- Invoices ----------------

export function useCreateInvoice() {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch<{ invoice: { id: string } }>("/invoices", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["invoices"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success("Invoice created"); },
    onError: (e) => onError(e, "Failed to create invoice"),
  });
}

export function useUpdateInvoice(id: string) {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch<{ invoice: unknown }>(`/invoices/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["invoices"] }); qc.invalidateQueries({ queryKey: ["invoices", id] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success("Invoice updated"); },
    onError: (e) => onError(e, "Failed to update invoice"),
  });
}

export function useDeleteInvoice() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/invoices/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["invoices"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success("Invoice deleted"); },
    onError: (e) => onError(e, "Failed to delete invoice"),
  });
}

// ---------------- Documents ----------------

export function usePresignDocument() {
  return useMutation({
    mutationFn: (body: { fileName: string; mimeType?: string; sizeBytes?: number; patientId?: string; visitId?: string; type?: string }) =>
      apiFetch<{ presignedUrl: string; r2Key: string }>("/documents/presign", { method: "POST", body: JSON.stringify(body) }),
    onError: (e) => onError(e, "Failed to presign upload"),
  });
}

export function useFinalizeDocument() {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch<{ document: { id: string } }>("/documents/finalize", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["documents"] }); toast.success("Document saved"); },
    onError: (e) => onError(e, "Failed to finalize document"),
  });
}

export function useDeleteDocument() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/documents/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["documents"] }); toast.success("Document deleted"); },
    onError: (e) => onError(e, "Failed to delete document"),
  });
}

export function useUploadToR2() {
  return useMutation({
    mutationFn: ({ key, contentType, base64 }: { key: string; contentType?: string; base64: string }) =>
      apiFetch<{ key: string }>("/documents/r2-upload", { method: "POST", body: JSON.stringify({ key, contentType, base64 }) }),
    onError: (e) => onError(e, "Failed to upload to R2"),
  });
}

// ---------------- AI ----------------

export function useAiIcd10() {
  return useMutation({
    mutationFn: (body: { diagnosis: string; patientContext?: { age?: number; sex?: string; chronicConditions?: string[] } }) =>
      apiFetch<{ codes: { code: string; description: string }[]; message?: string }>("/ai/icd10", { method: "POST", body: JSON.stringify(body) }),
    onError: (e) => onError(e, "AI request failed"),
  });
}

// ICD-10 catalog SEARCH (manual-entry picker) — POST /ai/icd10-search.
// Debounced by the caller (ManualIcd10SearchDialog: >= 2 chars, 500ms);
// server-side rate limit 20/min/user. Same shape as useAiIcd10.
export function useAiIcd10Search() {
  return useMutation({
    mutationFn: (body: { query: string }) =>
      apiFetch<{ codes: { code: string; description: string }[]; message?: string }>("/ai/icd10-search", { method: "POST", body: JSON.stringify(body) }),
    onError: (e) => onError(e, "AI search failed"),
  });
}

export function useAiPolish() {
  return useMutation({
    mutationFn: (body: { text: string; aiContext?: string }) =>
      apiFetch<{ text: string; message?: string }>("/ai/polish", { method: "POST", body: JSON.stringify(body) }),
    onError: (e) => onError(e, "AI polish failed"),
  });
}

export function useAiTest() {
  return useMutation({
    // model: optional override — test the DISPLAYED model (the UI passes the
    // currently-picked slug). Undefined → {} → backend tests the saved model.
    mutationFn: (model?: string) =>
      apiFetch<{ ok: boolean; message?: string; model?: string }>("/ai/test", {
        method: "POST",
        body: JSON.stringify(model ? { model } : {}),
      }),
    onError: (e) => onError(e, "AI test failed"),
  });
}

export function useTranscribe() {
  return useMutation({
    // NOTE: the route's zod schema expects { audio, format, language? } —
    // the base64 payload field is `audio`, NOT `data` (a mismatch here 400s
    // every dictation request; see post-mortem 2026-10-09).
    mutationFn: (audio: { audio: string; format: string; language?: string }) =>
      apiFetch<{ text: string; message?: string }>("/ai/transcribe", {
        method: "POST",
        body: JSON.stringify(audio),
      }),
    onError: (e) => onError(e, "Transcription failed"),
  });
}

export function useTranscribeTest() {
  return useMutation({
    // model: optional override — test the DISPLAYED transcription model.
    // Undefined → {} → backend tests the saved model.
    mutationFn: (model?: string) =>
      apiFetch<{ ok: boolean; message?: string; model?: string }>("/ai/transcribe-test", {
        method: "POST",
        body: JSON.stringify(model ? { model } : {}),
      }),
    onError: (e) => onError(e, "Transcription test failed"),
  });
}

// ---------------- Slot config versions (Phase 4, 2026-10-18) ----------------
//
// Draft CRUD + operational PATCH + wizard steps (backup trigger/status poll +
// transactional apply). All mutations invalidate the ["slot-configs"] list AND
// the per-date ["slot-config-effective", date] family (Phase 3 flagged this:
// queue pages + dialogs read the effective config with a 5-min staleTime).

export interface SlotConfigPayload {
  effectiveFrom: string;
  latePolicy: "first" | "in_order" | "last";
  slots: { key: string; label: string; startTime: string; endTime: string; checkinStart: string; checkinEnd: string; maxAppts: number; sortOrder: number }[];
}

export function useCreateSlotConfig() {
  const qc = q();
  return useMutation({
    mutationFn: (body: SlotConfigPayload) =>
      apiFetch<{ id: string }>("/slot-configs", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["slot-configs"] });
      toast.success("Draft config created");
    },
    onError: (e) => onError(e, "Failed to create draft config"),
  });
}

// Remap report shape shared by apply / pending-plan PATCH / pending-plan
// DELETE (mirrors the API responses).
export interface SlotConfigRemapReport {
  affected: number;
  perAppointment: { id: string; patientName: string; oldSlot: string | null; newSlot: string | null; queueOrder: number | null }[];
  capacity: Record<string, { booked: number; max: number; over: boolean }>;
  mismatches: { id: string; expectedSlot: string; actualSlot: string | null }[];
}

// PATCH /slot-configs/:id — structural edit. DRAFTS: plain save. PENDING
// PLANS (applied + future-effective): server-side fresh-backup guard +
// transactional re-remap of every Pending/Booked appointment from
// MIN(old, new effectiveFrom) — response carries the remap report.
export interface SlotConfigUpdateResult {
  updated?: boolean;
  id: string;
  effectiveFrom: string;
  latePolicy: SlotConfigPayload["latePolicy"];
  appliedAt: string | null;
  slots?: SlotConfigPayload["slots"];
  remap?: SlotConfigRemapReport;
}

export function useUpdateSlotConfig() {
  const qc = q();
  return useMutation({
    mutationFn: ({ id, body, assignments }: { id: string; body: Partial<SlotConfigPayload>; assignments?: { appointmentId: string; slotKey: string }[] }) =>
      apiFetch<SlotConfigUpdateResult>(`/slot-configs/${id}`, {
        method: "PATCH",
        body: JSON.stringify(assignments ? { ...body, assignments } : body),
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["slot-configs"] });
      if (res.remap) {
        // Pending-plan edit: appointments moved — invalidate the whole
        // config-derived cache family (same as apply).
        qc.invalidateQueries({ queryKey: ["slot-config-effective"] });
        qc.invalidateQueries({ queryKey: ["appointments"] });
        qc.invalidateQueries({ queryKey: ["dashboard"] });
        toast.success(`Plan saved — ${res.remap.affected} appointment${res.remap.affected === 1 ? "" : "s"} re-mapped`);
      } else {
        toast.success("Draft config saved");
      }
    },
    onError: (e) => onError(e, "Failed to save config"),
  });
}

// DELETE /slot-configs/:id — DRAFTS: plain delete. PENDING PLANS: fresh-backup
// guard + transactional revert-remap to the previous policy — response
// carries the revert report.
export interface SlotConfigDeleteResult {
  ok?: boolean;
  deleted?: boolean;
  id?: string;
  effectiveFrom?: string;
  reverted?: SlotConfigRemapReport;
}

export function useDeleteSlotConfig() {
  const qc = q();
  return useMutation({
    // assignments (2026-10-18): manual slot picks for needsRebooking rows in
    // the Delete dialog — the DELETE body carries them so rows that fit no
    // revert window can be assigned in-place instead of rebooked elsewhere.
    mutationFn: ({ id, assignments }: { id: string; assignments?: { appointmentId: string; slotKey: string }[] }) =>
      apiFetch<SlotConfigDeleteResult>(`/slot-configs/${id}`, {
        method: "DELETE",
        ...(assignments ? { body: JSON.stringify({ assignments }) } : {}),
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["slot-configs"] });
      if (res.reverted) {
        // Pending-plan delete: appointments reverted — invalidate the whole
        // config-derived cache family (same as apply).
        qc.invalidateQueries({ queryKey: ["slot-config-effective"] });
        qc.invalidateQueries({ queryKey: ["appointments"] });
        qc.invalidateQueries({ queryKey: ["dashboard"] });
        toast.success(`Plan deleted — ${res.reverted.affected} appointment${res.reverted.affected === 1 ? "" : "s"} reverted to the previous policy`);
      } else {
        toast.success("Draft config deleted");
      }
    },
    onError: (e) => onError(e, "Failed to delete config"),
  });
}

// Operational PATCH (admin-only server-side): latePolicy + per-slot check-in
// windows on an APPLIED config. Window edits never move appointments, so
// only the effective-config cache needs invalidation (not appointments).
export function useUpdateSlotConfigOperational() {
  const qc = q();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { latePolicy?: "first" | "in_order" | "last"; slots?: { key: string; checkinStart: string; checkinEnd: string }[] } }) =>
      apiFetch(`/slot-configs/${id}/operational`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["slot-configs"] });
      qc.invalidateQueries({ queryKey: ["slot-config-effective"] });
      toast.success("Operational settings saved");
    },
    onError: (e) => onError(e, "Failed to save operational settings"),
  });
}

// Wizard step 2: trigger the backup worker (does NOT wait — container job).
export function useTriggerSlotConfigBackup() {
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ triggeredAt: string }>(`/slot-configs/${id}/backup`, { method: "POST" }),
    onError: (e) => onError(e, "Failed to trigger backup"),
  });
}

// Wizard step 3: the transactional apply. On success the whole config-derived
// cache family is invalidated: the versions list, every per-date effective
// config (queue pages + dialogs read them with a 5-min staleTime), and the
// appointments lists (remapped slots + recomputed queue orders).
export interface SlotConfigApplyResult {
  applied: boolean;
  configId: string;
  effectiveFrom: string;
  affected: number;
  perAppointment: { id: string; patientName: string; oldSlot: string | null; newSlot: string | null; queueOrder: number | null }[];
  capacity: Record<string, { booked: number; max: number; over: boolean }>;
  mismatches: { id: string; expectedSlot: string; actualSlot: string | null }[];
}

export function useApplySlotConfig() {
  const qc = q();
  return useMutation({
    mutationFn: ({ id, assignments }: { id: string; assignments?: { appointmentId: string; slotKey: string }[] }) =>
      apiFetch<SlotConfigApplyResult>(`/slot-configs/${id}/apply`, {
        method: "POST",
        body: JSON.stringify({ assignments: assignments ?? [] }),
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["slot-configs"] });
      qc.invalidateQueries({ queryKey: ["slot-config-effective"] });
      qc.invalidateQueries({ queryKey: ["appointments"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(`Config applied — ${res.affected} appointment${res.affected === 1 ? "" : "s"} remapped`);
    },
    onError: (e) => onError(e, "Failed to apply config"),
  });
}

// ---------------- AI assignment suggestions (Phase 4, 2026-10-18; hybrid refine 2026-10-18) ----------------
//
// Admin-only. POST /ai/suggest-assignments — the apply wizard's Step 1
// "Refine with AI" button. The server re-runs the preview mapping, sends the
// draft's slot defs + per-slot mapped counts + needsAssignment rows to the
// model, and defensively validates the returned assignments. Optional
// currentAssignments (the wizard's current assignments state) switches the
// model to REVIEW/ADJUST mode instead of generating from scratch. Rows the
// model couldn't assign come back with slotKey: null. AI-not-configured and
// OpenRouter failures BOTH return 200 with error: "ai_unavailable" (graceful
// degradation — the wizard shows the message instead of failing).
export interface AssignmentSuggestion {
  appointmentId: string;
  slotKey: string | null;
  reason: string;
}

export interface SuggestAssignmentsResult {
  assignments: AssignmentSuggestion[];
  counts: { total: number; mapped: number; needsAssignment: number };
  model?: string;
  message?: string;
  error?: string;
}

export function useSuggestAssignments() {
  return useMutation({
    // flow extensions (2026-10-18): proposedSlots/proposedEffectiveFrom =
    // pending-plan EDIT (suggestions against the proposed windows);
    // mode: "revert" = pending-plan DELETE (suggestions against the previous
    // policy's per-date revert targets). Omitted = the apply wizard's draft
    // flow (unchanged).
    mutationFn: ({
      configId,
      currentAssignments,
      proposedSlots,
      proposedEffectiveFrom,
      mode,
    }: {
      configId: string;
      currentAssignments?: { appointmentId: string; slotKey: string }[];
      proposedSlots?: SlotConfigPayload["slots"];
      proposedEffectiveFrom?: string;
      mode?: "revert";
    }) =>
      apiFetch<SuggestAssignmentsResult>("/ai/suggest-assignments", {
        method: "POST",
        body: JSON.stringify({
          configId,
          currentAssignments: currentAssignments ?? [],
          ...(proposedSlots ? { proposedSlots, proposedEffectiveFrom } : {}),
          ...(mode ? { mode } : {}),
        }),
      }),
    onError: (e) => onError(e, "AI suggestion request failed"),
  });
}

// Instant deterministic auto-fill (2026-10-18 hybrid design): POST
// /slot-configs/:id/suggest-assignments — nearest-window + headroom
// arithmetic, NO AI, completes in well under a second. scheduling.manage
// gated (same audience as the preview). Response mirrors the AI route.
// Same flow extensions as the AI route (proposedSlots for edit, mode:
// "revert" for delete).
export function useAutoFillAssignments() {
  return useMutation({
    mutationFn: ({
      configId,
      proposedSlots,
      proposedEffectiveFrom,
      mode,
    }: {
      configId: string;
      proposedSlots?: SlotConfigPayload["slots"];
      proposedEffectiveFrom?: string;
      mode?: "revert";
    }) =>
      apiFetch<SuggestAssignmentsResult>(`/slot-configs/${configId}/suggest-assignments`, {
        method: "POST",
        body: JSON.stringify({
          ...(proposedSlots ? { proposedSlots, proposedEffectiveFrom } : {}),
          ...(mode ? { mode } : {}),
        }),
      }),
    onError: (e) => onError(e, "Auto-fill request failed"),
  });
}

// ---------------- Block Days reschedule flow (2026-10-29) ----------------
// The Block Days flow's OWN suggestion mutations — SEPARATE from the
// slot-config wizard's suggest-assignments routes above (no shared state).
// POST /block-reschedule/suggest      — deterministic auto-fill (no AI)
// POST /block-reschedule/suggest-ai   — AI proposals (admin-only server-side)
// Both are read-only; the actual block+move write stays POST /slot-blocks.

export interface BlockRescheduleSuggestion {
  id: string;
  patientName?: string;
  clinicUhid?: string;
  status?: string;
  slot?: string | null;
  arrivalTime?: string | null;
  newDate: string | null;
  newSlot: string | null;
  /** The suggested slot's startTime — default arrival time for the row. */
  newStartTime?: string | null;
  reason?: string;
}

export interface BlockRescheduleSuggestResult {
  suggestions: BlockRescheduleSuggestion[];
  message?: string;
  error?: string;
  model?: string;
}

export function useBlockRescheduleSuggest() {
  return useMutation({
    mutationFn: ({ date, slot }: { date: string; slot: string | null }) =>
      apiFetch<BlockRescheduleSuggestResult>("/block-reschedule/suggest", {
        method: "POST",
        body: JSON.stringify({ date, slot }),
      }),
    onError: (e) => onError(e, "Auto-fill failed"),
  });
}

export function useBlockRescheduleSuggestAi() {
  return useMutation({
    mutationFn: ({
      date,
      slot,
      currentAssignments,
    }: {
      date: string;
      slot: string | null;
      currentAssignments?: { appointmentId: string; newDate: string; newSlot: string | null }[];
    }) =>
      apiFetch<BlockRescheduleSuggestResult>("/block-reschedule/suggest-ai", {
        method: "POST",
        body: JSON.stringify({ date, slot, currentAssignments: currentAssignments ?? [] }),
      }),
    onError: (e) => onError(e, "AI suggestion request failed"),
  });
}

// ---------------- Referring Doctors ----------------

export function useCreateReferringDoctor() {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch("/referring-doctors", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["referring-doctors"] }); toast.success("Doctor added"); },
    onError: (e) => onError(e, "Failed to add doctor"),
  });
}

export function useUpdateReferringDoctor() {
  const qc = q();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => apiFetch(`/referring-doctors/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["referring-doctors"] }); toast.success("Doctor updated"); },
    onError: (e) => onError(e, "Failed to update doctor"),
  });
}

export function useDeleteReferringDoctor() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/referring-doctors/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["referring-doctors"] }); toast.success("Doctor deleted"); },
    onError: (e) => onError(e, "Failed to delete doctor"),
  });
}

// ---------------- Clinic Doctors ----------------

export function useCreateClinicDoctor() {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch("/clinic-doctors", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["clinic-doctors"] }); toast.success("Doctor added"); },
    onError: (e) => onError(e, "Failed to add doctor"),
  });
}

export function useUpdateClinicDoctor() {
  const qc = q();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => apiFetch(`/clinic-doctors/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["clinic-doctors"] }); toast.success("Doctor updated"); },
    onError: (e) => onError(e, "Failed to update doctor"),
  });
}

export function useDeleteClinicDoctor() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/clinic-doctors/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["clinic-doctors"] }); toast.success("Doctor deleted"); },
    onError: (e) => onError(e, "Failed to delete doctor"),
  });
}

// ---------------- Referral Letters ----------------

export function useCreateReferralLetter() {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch("/referrals", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["referrals"] }); toast.success("Referral letter saved"); },
    onError: (e) => onError(e, "Failed to save referral letter"),
  });
}

export function useUpdateReferralLetter(id: string) {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch(`/referrals/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["referrals"] }); qc.invalidateQueries({ queryKey: ["referrals", id] }); toast.success("Referral letter updated"); },
    onError: (e) => onError(e, "Failed to update referral letter"),
  });
}

export function useDeleteReferralLetter() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/referrals/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["referrals"] }); toast.success("Referral letter deleted"); },
    onError: (e) => onError(e, "Failed to delete referral letter"),
  });
}

export function useSendReferralWebhook() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/referrals/${id}/send-webhook`, { method: "POST" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["referrals"] }); toast.success("Webhook sent"); },
    onError: (e) => onError(e, "Failed to send webhook"),
  });
}

// ---------------- Medical Certificates ----------------

export function useCreateMedicalCertificate() {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch("/certificates", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["certificates"] }); toast.success("Certificate saved"); },
    onError: (e) => onError(e, "Failed to save certificate"),
  });
}

export function useUpdateMedicalCertificate(id: string) {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch(`/certificates/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["certificates"] }); qc.invalidateQueries({ queryKey: ["certificates", id] }); toast.success("Certificate updated"); },
    onError: (e) => onError(e, "Failed to update certificate"),
  });
}

export function useDeleteMedicalCertificate() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/certificates/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["certificates"] }); toast.success("Certificate deleted"); },
    onError: (e) => onError(e, "Failed to delete certificate"),
  });
}

export function useSendCertificateWebhook() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/certificates/${id}/send-webhook`, { method: "POST" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["certificates"] }); toast.success("Webhook sent"); },
    onError: (e) => onError(e, "Failed to send webhook"),
  });
}

// ---------------- Investigations catalog ----------------

export function useCreateInvestigation() {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch("/investigations", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["investigations"] }); qc.invalidateQueries({ queryKey: ["investigation-categories"] }); toast.success("Investigation added"); },
    onError: (e) => onError(e, "Failed to add investigation"),
  });
}

export function useUpdateInvestigation() {
  const qc = q();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => apiFetch(`/investigations/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["investigations"] }); qc.invalidateQueries({ queryKey: ["investigation-categories"] }); toast.success("Investigation updated"); },
    onError: (e) => onError(e, "Failed to update investigation"),
  });
}

export function useDeleteInvestigation() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/investigations/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["investigations"] }); qc.invalidateQueries({ queryKey: ["investigation-categories"] }); toast.success("Investigation deleted"); },
    onError: (e) => onError(e, "Failed to delete investigation"),
  });
}

export function useBulkDeleteInvestigations() {
  const qc = q();
  return useMutation({
    mutationFn: (ids: string[]) => apiFetch<{ deleted: number; requested: number }>("/investigations/bulk-delete", { method: "POST", body: JSON.stringify({ ids }) }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["investigations"] });
      qc.invalidateQueries({ queryKey: ["investigation-templates"] });
      qc.invalidateQueries({ queryKey: ["investigation-categories"] });
      toast.success(`Deleted ${res.deleted} investigation${res.deleted === 1 ? "" : "s"}`);
    },
    onError: (e) => onError(e, "Failed to delete investigations"),
  });
}

export function useBulkImportInvestigations() {
  const qc = q();
  return useMutation({
    mutationFn: (rows: { name: string; category?: string }[]) => apiFetch<{ inserted: number; updated: number; total: number }>("/investigations/bulk", { method: "POST", body: JSON.stringify({ rows }) }),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ["investigations"] }); qc.invalidateQueries({ queryKey: ["investigation-categories"] }); toast.success(`Bulk import complete: ${res.inserted} inserted, ${res.updated} updated`); },
    onError: (e) => onError(e, "Bulk import failed"),
  });
}

export function useCreateInvestigationTemplate() {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch("/investigations/templates", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["investigation-templates"] }); toast.success("Template created"); },
    onError: (e) => onError(e, "Failed to create template"),
  });
}

export function useUpdateInvestigationTemplate() {
  const qc = q();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => apiFetch(`/investigations/templates/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["investigation-templates"] }); toast.success("Template updated"); },
    onError: (e) => onError(e, "Failed to update template"),
  });
}

export function useDeleteInvestigationTemplate() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/investigations/templates/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["investigation-templates"] }); toast.success("Template deleted"); },
    onError: (e) => onError(e, "Failed to delete template"),
  });
}

// ---------------- Investigation Template Groups (migration 0037, 2026-11-04) ----------------
// Mirrors the prescription template-group hooks 1:1 (see
// useCreatePrescriptionTemplateGroup et al. below).

export function useCreateInvestigationTemplateGroup() {
  const qc = q();
  return useMutation({
    mutationFn: (body: { name: string }) =>
      apiFetch("/investigations/template-groups", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["investigation-template-groups"] });
      toast.success("Group created");
    },
    onError: (e) => onError(e, "Failed to create group"),
  });
}

export function useUpdateInvestigationTemplateGroup() {
  const qc = q();
  return useMutation({
    // Id passed per call (the Groups tab edits arbitrary rows).
    mutationFn: ({ id, name, updatedAt }: { id: string; name: string; updatedAt?: string }) =>
      apiFetch(`/investigations/template-groups/${id}`, { method: "PATCH", body: JSON.stringify({ name, updatedAt }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["investigation-template-groups"] });
      toast.success("Group renamed");
    },
    onError: (e) => onError(e, "Failed to rename group"),
  });
}

export function useDeleteInvestigationTemplateGroup() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/investigations/template-groups/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      // Both lists change: the group disappears AND its members' groupId
      // becomes null (they land in "Ungrouped" server-side via SET NULL).
      qc.invalidateQueries({ queryKey: ["investigation-template-groups"] });
      qc.invalidateQueries({ queryKey: ["investigation-templates"] });
      toast.success("Group deleted — its templates moved to Ungrouped");
    },
    onError: (e) => onError(e, "Failed to delete group"),
  });
}

// Move a template to another group (or to Ungrouped with null). Uses the
// template update mutation against the same PATCH endpoint with only
// groupId set.
export function useMoveInvestigationTemplate() {
  const qc = q();
  return useMutation({
    mutationFn: ({ id, groupId }: { id: string; groupId: string | null }) =>
      apiFetch(`/investigations/templates/${id}`, { method: "PATCH", body: JSON.stringify({ groupId }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["investigation-templates"] }); toast.success("Template moved"); },
    onError: (e) => onError(e, "Failed to move template"),
  });
}

export function useCreateInvestigationCategory() {
  const qc = q();
  return useMutation({
    mutationFn: (name: string) => apiFetch("/investigations/categories", { method: "POST", body: JSON.stringify({ name }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["investigation-categories"] }); toast.success("Category added"); },
    onError: (e) => onError(e, "Failed to add category"),
  });
}

export function useDeleteInvestigationCategory() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/investigations/categories/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["investigation-categories"] }); toast.success("Category deleted"); },
    onError: (e) => onError(e, "Failed to delete category"),
  });
}

// ---------------- Patient Info Templates ----------------

export function useCreatePatientInfoTemplate() {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch("/patient-info-templates", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["patient-info-templates"] }); toast.success("Template created"); },
    onError: (e) => onError(e, "Failed to create template"),
  });
}

export function useUpdatePatientInfoTemplate(id: string) {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch(`/patient-info-templates/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["patient-info-templates"] }); toast.success("Template updated"); },
    onError: (e) => onError(e, "Failed to update template"),
  });
}

export function useDeletePatientInfoTemplate() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/patient-info-templates/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["patient-info-templates"] }); toast.success("Template deleted"); },
    onError: (e) => onError(e, "Failed to delete template"),
  });
}

// ---------------- Prescription Templates ----------------

export function useCreatePrescriptionTemplate() {
  const qc = q();
  return useMutation({
    mutationFn: (body: { name: string; medicines: TemplateMedicine[]; groupId?: string | null }) =>
      apiFetch("/prescription-templates", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["prescription-templates"] }); toast.success("Template saved"); },
    onError: (e) => onError(e, "Failed to save template"),
  });
}

export function useUpdatePrescriptionTemplate(id: string) {
  const qc = q();
  return useMutation({
    mutationFn: (body: { name?: string; medicines?: TemplateMedicine[]; groupId?: string | null; updatedAt?: string }) =>
      apiFetch(`/prescription-templates/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["prescription-templates"] }); toast.success("Template updated"); },
    onError: (e) => onError(e, "Failed to update template"),
  });
}

export function useDeletePrescriptionTemplate() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/prescription-templates/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["prescription-templates"] }); toast.success("Template deleted"); },
    onError: (e) => onError(e, "Failed to delete template"),
  });
}

// ---------------- Prescription Template Groups (migration 0036) ----------------

export function useCreatePrescriptionTemplateGroup() {
  const qc = q();
  return useMutation({
    mutationFn: (body: { name: string }) =>
      apiFetch("/prescription-template-groups", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prescription-template-groups"] });
      toast.success("Group created");
    },
    onError: (e) => onError(e, "Failed to create group"),
  });
}

export function useUpdatePrescriptionTemplateGroup() {
  const qc = q();
  return useMutation({
    // Id passed per call (the Groups tab edits arbitrary rows).
    mutationFn: ({ id, name, updatedAt }: { id: string; name: string; updatedAt?: string }) =>
      apiFetch(`/prescription-template-groups/${id}`, { method: "PATCH", body: JSON.stringify({ name, updatedAt }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prescription-template-groups"] });
      toast.success("Group renamed");
    },
    onError: (e) => onError(e, "Failed to rename group"),
  });
}

export function useDeletePrescriptionTemplateGroup() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/prescription-template-groups/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      // Both lists change: the group disappears AND its members' groupId
      // becomes null (they land in "Ungrouped" server-side via SET NULL).
      qc.invalidateQueries({ queryKey: ["prescription-template-groups"] });
      qc.invalidateQueries({ queryKey: ["prescription-templates"] });
      toast.success("Group deleted — its templates moved to Ungrouped");
    },
    onError: (e) => onError(e, "Failed to delete group"),
  });
}

// Move a template to another group (or to Ungrouped with null). Uses the
// update mutation against the same PATCH endpoint with only groupId set.
export function useMovePrescriptionTemplate() {
  const qc = q();
  return useMutation({
    mutationFn: ({ id, groupId }: { id: string; groupId: string | null }) =>
      apiFetch(`/prescription-templates/${id}`, { method: "PATCH", body: JSON.stringify({ groupId }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["prescription-templates"] }); toast.success("Template moved"); },
    onError: (e) => onError(e, "Failed to move template"),
  });
}

// ---------------- App Settings (covers all field groups) ----------------

export function useUpdateAppSettings() {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch<{ settings: unknown }>("/settings", { method: "PATCH", body: JSON.stringify(body) }),
    // Invalidate BOTH settings queries: the gated one (Settings page,
    // referral/certificate editors) and the public one (Sidebar/Topbar +
    // booking/EMR dialogs) — a saved edit must reach every consumer.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["app-settings"] });
      qc.invalidateQueries({ queryKey: ["app-settings-public"] });
      toast.success("Settings saved");
    },
    onError: (e) => onError(e, "Failed to save settings"),
  });
}

// ---------------- Admin Users ----------------

export function useCreateUser() {
  const qc = q();
  return useMutation({
    mutationFn: (body: { email: string; password: string; fullName: string; role: "admin" | "user"; permissionIds?: string[] }) =>
      apiFetch<{ id: string }>("/admin/users", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "users"] }); toast.success("User created"); },
    onError: (e) => onError(e, "Failed to create user"),
  });
}

export function useUpdateUser(id: string) {
  const qc = q();
  return useMutation({
    mutationFn: (body: { fullName?: string; role?: "admin" | "user" }) =>
      apiFetch(`/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "users"] }); toast.success("User updated"); },
    onError: (e) => onError(e, "Failed to update user"),
  });
}

export function useDeleteUser() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/admin/users/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "users"] }); toast.success("User deleted"); },
    onError: (e) => onError(e, "Failed to delete user"),
  });
}

export function useResetUserPassword() {
  const qc = q();
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password?: string }) =>
      apiFetch<{ tempPassword: string }>(`/admin/users/${id}/reset-password`, { method: "POST", body: JSON.stringify({ password }) }),
    onSuccess: (data) => { qc.invalidateQueries({ queryKey: ["admin", "users"] }); toast.success("Password reset"); },
    onError: (e) => onError(e, "Failed to reset password"),
  });
}

// ---------------- Discharge Summaries (admin-only) ----------------

export function useCreateDischargeSummary() {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch("/discharge-summaries", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["discharge-summaries"] }); toast.success("Discharge summary saved"); },
    onError: (e) => onError(e, "Failed to save discharge summary"),
  });
}

export function useUpdateDischargeSummary(id: string) {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch(`/discharge-summaries/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["discharge-summaries"] });
      qc.invalidateQueries({ queryKey: ["discharge-summaries", id] });
      toast.success("Discharge summary updated");
    },
    onError: (e) => onError(e, "Failed to update discharge summary"),
  });
}

export function useDeleteDischargeSummary() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/discharge-summaries/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["discharge-summaries"] }); toast.success("Discharge summary deleted"); },
    onError: (e) => onError(e, "Failed to delete discharge summary"),
  });
}

export function useCreateDischargeSectionTemplate() {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch("/discharge-summaries/templates", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["discharge-summaries", "templates"] }); toast.success("Template created"); },
    onError: (e) => onError(e, "Failed to create template"),
  });
}

export function useUpdateDischargeSectionTemplate(id: string) {
  const qc = q();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiFetch(`/discharge-summaries/templates/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["discharge-summaries", "templates"] }); toast.success("Template updated"); },
    onError: (e) => onError(e, "Failed to update template"),
  });
}

export function useDeleteDischargeSectionTemplate() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/discharge-summaries/templates/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["discharge-summaries", "templates"] }); toast.success("Template deleted"); },
    onError: (e) => onError(e, "Failed to delete template"),
  });
}

export function useToggleUserActive(id: string) {
  const qc = q();
  return useMutation({
    mutationFn: () => apiFetch<{ ok: boolean; isActive: boolean }>(`/admin/users/${id}/activate`, { method: "PATCH" }),
    onSuccess: (data) => { qc.invalidateQueries({ queryKey: ["admin", "users"] }); toast.success(data.isActive ? "User activated" : "User deactivated"); },
    onError: (e) => onError(e, "Failed to toggle user status"),
  });
}

export function useForceLogoutUser(id: string) {
  const qc = q();
  return useMutation({
    mutationFn: () => apiFetch(`/admin/users/${id}/force-logout`, { method: "POST" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "users"] }); toast.success("User signed out"); },
    onError: (e) => onError(e, "Failed to force logout"),
  });
}

export function useSetUserPermissions(id: string) {
  const qc = q();
  return useMutation({
    mutationFn: (body: { permissionIds: string[]; templateId?: string }) =>
      apiFetch(`/admin/users/${id}/permissions`, { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "users"] }); toast.success("Permissions updated"); },
    onError: (e) => onError(e, "Failed to update permissions"),
  });
}

// ---------------- Permission Templates ----------------

export function useCreateTemplate() {
  const qc = q();
  return useMutation({
    mutationFn: (body: { name: string; description?: string; permissionIds: string[] }) =>
      apiFetch<{ id: string }>("/permissions/templates", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["permission-templates"] }); toast.success("Template created"); },
    onError: (e) => onError(e, "Failed to create template"),
  });
}

export function useUpdateTemplate() {
  const qc = q();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { name?: string; description?: string; permissionIds?: string[] } }) =>
      apiFetch(`/permissions/templates/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["permission-templates"] }); toast.success("Template updated"); },
    onError: (e) => onError(e, "Failed to update template"),
  });
}

export function useDeleteTemplate() {
  const qc = q();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/permissions/templates/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["permission-templates"] }); toast.success("Template deleted"); },
    onError: (e) => onError(e, "Failed to delete template"),
  });
}

// ---------------- Nav Pins (sidebar pinning, per-user) ----------------

export function useReplaceNavPins() {
  const qc = q();
  return useMutation({
    mutationFn: (routes: string[]) =>
      apiFetch<{ routes: string[] }>("/nav-pins", { method: "PUT", body: JSON.stringify({ routes }) }),
    // No toast on success — pin toggles are quiet, frequent interactions.
    onError: (e) => onError(e, "Failed to save pinned items"),
  });
}

// ---------------- Error Log (admin-only manual purge) ----------------

export function useClearErrorLog() {
  const qc = q();
  return useMutation({
    mutationFn: () => apiFetch<{ cleared: number }>("/error-log", { method: "DELETE" }),
    onSuccess: ({ cleared }) => {
      // Invalidate the whole ["error-log"] group: the rows list (every
      // filter/page combination) and the codes dropdown both read from it.
      qc.invalidateQueries({ queryKey: ["error-log"] });
      toast.success(`Cleared ${cleared} error log ${cleared === 1 ? "entry" : "entries"}`);
    },
    onError: (e) => onError(e, "Failed to clear error log"),
  });
}

// ---------------- Dictation button position (per-user, DB-backed) -----------

// ---------------- Backups (admin-only delete) ----------------

// POST /backups/delete — delete explicit backup keys via the emr-backup
// Worker. Server audits the action (BACKUPS_DELETE with dates).
export function useDeleteBackups() {
  const qc = q();
  return useMutation({
    mutationFn: (keys: string[]) =>
      apiFetch<{ deleted: string[]; missing: string[] }>("/backups/delete", {
        method: "POST",
        body: JSON.stringify({ keys }),
      }),
    onSuccess: ({ deleted, missing }) => {
      qc.invalidateQueries({ queryKey: ["backups"] });
      const parts = [`Deleted ${deleted.length} backup${deleted.length === 1 ? "" : "s"}`];
      if (missing.length > 0) parts.push(`${missing.length} already gone`);
      toast.success(parts.join(" · "));
    },
    onError: (e) => onError(e, "Failed to delete backup(s)"),
  });
}

export function useSaveDictationPos() {
  const qc = q();
  return useMutation({
    mutationFn: (pos: { x: number; y: number }) =>
      apiFetch<{ x: number; y: number }>("/dictation-pos", { method: "PUT", body: JSON.stringify(pos) }),
    // Keep the query cache in sync with what the server now holds, so a
    // later remount/refetch doesn't yank the button back to a stale position.
    onMutate: ({ x, y }) => {
      qc.setQueryData(["dictation-pos"], { x, y });
    },
    // No toast on success — a drag is a quiet interaction (mirrors nav pins).
    onError: (e) => onError(e, "Couldn't save button position"),
  });
}
